<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :is-open="confirmCreateJobDialog"
            :data="currentOrder"
            :action-loading="actionLoading.createJob"
            @on-confirm="createJob"
            @on-abort="closeConfirmCreateJobDialog"
        >
            <template #title>
                Tworzenie zlecenia
            </template>
            <template #content>
                Czy na pewno chcesz utworzyć zlecenie?
            </template>
            <template #confirmButton>
                Utwórz
            </template>
        </ConfirmDialog>

        <ConfirmDialog
            :is-open="confirmMarkOrderAsClosedDialog"
            :data="currentOrder"
            :action-loading="actionLoading.markOrderAsClosed"
            @on-confirm="markOrderAsClosed"
            @on-abort="closeConfirmMarkOrderAsClosedDialog"
        >
            <template #title>
                Zmiana statusu
            </template>
            <template #content>
                Czy na pewno chcesz zamknąć to zamówienie?
            </template>
            <template #confirmButton>
                Zatwierdź
            </template>
        </ConfirmDialog>

        <ConfirmDialog
            :is-open="confirmDeleteOrderDialog"
            :data="currentOrder"
            :action-loading="actionLoading.deleteOrder"
            removal
            @on-confirm="deleteOrder"
            @on-abort="closeConfirmDeleteOrderDialog"
        >
            <template #title>
                Usuwanie zamówienia
            </template>
            <template #content>
                Czy na pewno chcesz usunąć zamówienie?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <page-title>Lista zamówień komercyjnych</page-title>

        <v-row align="center">
            <v-col cols="12" sm="6" lg="4">
                <v-btn
                    color="secondary"
                    medium
                    data-sentry-label="Złóż zamówienie"
                    @click="createOrder"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Złóż zamówienie
                </v-btn>
            </v-col>

            <v-col cols="12" lg="8">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col sm="auto">
                            <v-checkbox
                                v-model="filters.status_id"
                                label="Ukryj zamknięte"
                                hide-details
                                class="mt-2"
                                true-value="10,15"
                                false-value=""
                                @change="changeFilter"
                            />
                        </v-col>
                        <v-col sm="auto">
                            <v-checkbox
                                v-model="filters.createdBy_id"
                                label="Pokaż moje"
                                hide-details
                                class="mt-2"
                                @change="changeFilter"
                            />
                        </v-col>
                        <v-col sm="auto">
                            <v-checkbox
                                v-model="isPlannedSendDateTodaySelected"
                                label="Wysyłka na dziś"
                                hide-details
                                class="mt-2"
                                @change="changeFilter"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="filters.productionNumber"
                                append-icon="mdi-magnify"
                                label="Szukaj..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('productionNumber')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="orders"
                        :loading="ordersLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :items-per-page.sync="pagination.itemsPerPage"
                        :mobile-breakpoint="0"
                        :item-class="setSelectedRow"
                        name="ordersData"
                        @pagination="updatePagination"
                    >
                        <template #item.productionNumber="{item}">
                            {{ item.productionNumber }}
                        </template>
                        <template #item.name="{item}">
                            {{ item.name }}
                        </template>
                        <template #item.customerName="{item}">
                            <template v-if="item.customer">
                                {{ item.customer.name }}
                            </template>
                            <template v-else>
                                -
                            </template>
                        </template>
                        <template #item.createdAt="{item}">
                            {{ formatDate(item.createdAt) }}
                        </template>
                        <template #item.plannedSendDate="{item}">
                            {{ formatDate(item.plannedSendDate) }}
                        </template>
                        <template #item.numberOfProducts="{item}">
                            {{ item.numberOfProducts }}
                        </template>
                        <template #item.status="{item}">
                            {{ item.status.name }}

                            <v-tooltip v-if="item.overdue" :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <v-icon color="warning" small v-on="on">
                                        mdi-clock-outline
                                    </v-icon>
                                </template>
                                <span>Opóźnione</span>
                            </v-tooltip>
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip v-if="item.orderToJobSchema && item.statusId === 10" :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <div v-on="on">
                                            <v-btn
                                                icon
                                                data-sentry-label="Tworzenie zlecenia"
                                                :disabled="!item.orderToJobAvailable"
                                                @click="openConfirmCreateJobDialog(item)"
                                            >
                                                <v-icon color="teal">
                                                    mdi-inbox-multiple
                                                </v-icon>
                                            </v-btn>
                                        </div>
                                    </template>
                                    <span v-if="item.orderToJobAvailable">Utwórz zlecenie</span>
                                    <span v-else>Aby utworzyć zlecenie uzupełnij dane zamówienia</span>
                                </v-tooltip>
                                <v-tooltip v-if="item.statusId === 15" :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <div v-on="on">
                                            <v-btn
                                                icon
                                                data-sentry-label="Potwierdzenie zamknięcia zamówienia"
                                                @click="openConfirmMarkOrderAsClosedDialog(item)"
                                            >
                                                <v-icon color="blue-grey">
                                                    mdi-cube-send
                                                </v-icon>
                                            </v-btn>
                                        </div>
                                    </template>
                                    <span>Zamknij zamówienie</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Szczegóły zamówienia"
                                            v-on="on"
                                            @click="openOrder(item.id)"
                                        >
                                            <v-icon color="blue">
                                                mdi-ballot
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Otwórz</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import router from '@/router'
import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import dateFormatter from '@/mixins/dateFormatter'
import dataTableClassSetter from '@/mixins/dataTableClassSetter'
import { errorHandler } from '@/utils/errorHandler'
import { hasUserPermission } from '@/utils/permissionsHandler'

export default {
    name: 'InternalOrders',
    components: {
        PageTitle, ConfirmDialog
    },
    mixins: [
        tooltipDelay,
        filters,
        pagination,
        dateFormatter,
        dataTableClassSetter
    ],
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('orders/clearInternalOrdersFilters')
            this.clearSelectedListItemId()
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            isPlannedSendDateTodaySelected: false,
            confirmCreateJobDialog: false,
            confirmMarkOrderAsClosedDialog: false,
            confirmDeleteOrderDialog: false,
            currentOrder: {},
            actionLoading: {
                createJob: false,
                markOrderAsClosed: false,
                deleteOrder: false
            },
            headers: [
                {
                    text: 'Numer zamówienia',
                    value: 'productionNumber',
                    sortable: false,
                    width: 200
                },
                {
                    text: 'Nazwa zamówienia',
                    value: 'name',
                    sortable: false,
                    align: 'left',
                },
                {
                    text: 'Klient',
                    value: 'customerName',
                    sortable: false,
                    align: 'left',
                },
                {
                    text: 'Data zamówienia',
                    value: 'createdAt',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Planowana data wysyłki',
                    value: 'plannedSendDate',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Ilość produktów',
                    value: 'numberOfProducts',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Status',
                    value: 'status',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'right',
                    width: 120
                }
            ]
        }
    },
    computed: {
        userId() {
            return this.$store.state.user.userId
        },
        isTraderUser() {
            return hasUserPermission('jobs_trader_update')
        },
        orders() {
            return this.$store.state.orders.order.orders
        },
        ordersLoading() {
            return this.$store.state.orders.order.ordersLoading
        },
        savedFilters() {
            return this.$store.state.orders.order.internalOrdersFilters
        },
        selectedOrderId() {
            return this.$store.state.common.selectedListItemId
        },
        filterQuery() {
            const filters = this.filters
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    if (item === 'createdBy_id') {
                        filters[item] = this.userId
                    }

                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        }
    },
    beforeMount() {
        if (!this.savedPagination.pagination) {
            this.resetPagination()
        }
    },
    mounted() {
        this.getOrders()
    },
    methods: {
        setSelectedRow(item) {
            if (item.id === this.selectedOrderId) {
                return 'isSelected'
            }
        },
        getOrders() {
            this.dateRangeHandle()
            this.$store.dispatch('orders/getOrders', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        createOrder() {
            router.push({ name: 'ordersInternalOrdersOrderCreate' })
                .catch(error => {
                    errorHandler(error)
                })
        },
        openOrder(orderId) {
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.setSelectedListItemId(orderId)
            router.push({
                name: 'ordersInternalOrdersOrder',
                params: {
                    id: orderId
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        createJob(order) {
            this.actionLoading.createJob = true
            this.$store.dispatch('jobs/createJob', order.orderToJobSchema)
                .then((response) => {
                    this.$store.dispatch('showNotification', {
                        type: 'success',
                        message: 'Zlecenie zostało utworzone.'
                    })

                    this.$store.dispatch('orders/updateOrder', {
                        id: order.id,
                        statusId: 15 // Zatwierdzone
                    })
                        .then(() => {
                            this.getOrders()
                            this.closeConfirmCreateJobDialog()
                            if (this.isTraderUser) {
                                this.$router.push({
                                    name: 'jobsJob',
                                    params: {
                                        jobId: response.data.id,
                                        autoClose: order.orderToJobSchema.autoJobClose
                                    }
                                })
                            }
                        })
                        .catch(error => {
                            errorHandler(error)
                        })
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.createJob = false
                })
        },
        openConfirmCreateJobDialog(order) {
            this.confirmCreateJobDialog = true
            this.currentOrder = order
        },
        closeConfirmCreateJobDialog() {
            this.confirmCreateJobDialog = false
            this.currentOrder = {}
        },
        markOrderAsClosed(order) {
            this.actionLoading.markOrderAsClosed = true

            this.$store.dispatch('orders/updateOrder', {
                id: order.id,
                statusId: 50 // Zamknięte
            })
                .then(() => {
                    this.getOrders()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.markOrderAsClosed = false
                    this.closeConfirmMarkOrderAsClosedDialog()
                })
        },
        openConfirmMarkOrderAsClosedDialog(order) {
            this.confirmMarkOrderAsClosedDialog = true
            this.currentOrder = order
        },
        closeConfirmMarkOrderAsClosedDialog() {
            this.confirmMarkOrderAsClosedDialog = false
            this.currentOrder = {}
        },
        deleteOrder(order) {
            this.actionLoading.deleteOrder = true
            this.$store.dispatch('orders/deleteOrder', order.id)
                .then(() => {
                    this.getOrders()
                    this.closeConfirmDeleteOrderDialog()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.deleteOrder = false
                })
        },
        openConfirmDeleteOrderDialog(order) {
            this.confirmDeleteOrderDialog = true
            this.currentOrder = order
        },
        closeConfirmDeleteOrderDialog() {
            this.confirmDeleteOrderDialog = false
            this.currentOrder = {}
        },
        dateRangeHandle() {
            if (this.isPlannedSendDateTodaySelected) {
                this.filters.plannedSendDate__gte = this.todayMinDate
                this.filters.plannedSendDate__lte = this.todayMaxDate
            } else {
                this.filters.plannedSendDate__gte = ''
                this.filters.plannedSendDate__lte = ''
            }
        },
        changeFilter() {
            this.$store.dispatch('orders/setInternalOrdersFilters', this.filters)
            this.resetPagination()
            this.getOrders()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && !this.savedPagination.pagination) {
                this.getOrders()
            }
            this.clearPaginationPending()
        },
    }
}
</script>
